import { PurchaseOrderService } from "src/services";
import { DD_MM_YYYY } from "src/helpers";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { PurchaseOrderColumnHeader, PurchaseOrderFileType, VendorFileType } from "src/enums";
import { PurchaseOrder, TableColumn } from "src/types";
import { TrackingLink } from "src/components";
import { StatusPill } from "src/components/ui";
import { Table } from "src/components";
import { Truck, FileText, Info, Receipt, Download } from "lucide-react";
import { Button } from "src/shadcn/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";
import { Link } from "react-router-dom";

interface VendorsPurchaseOrderDashTableProps {
  purchaseOrders: PurchaseOrder[];
  columnsToHide?: PurchaseOrderColumnHeader[];
  downloadFile: (purchaseOrderId: string, purchaseOrderFileType: PurchaseOrderFileType) => void;
  isFileDownloading?: boolean;
}

const VendorsPurchaseOrderDashTable: React.FC<VendorsPurchaseOrderDashTableProps> = ({
  purchaseOrders,
  columnsToHide,
  downloadFile,
  isFileDownloading
}) => {
  const purchaseOrdersTableColumns: TableColumn<PurchaseOrderColumnHeader, PurchaseOrder>[] = [
    {
      header: PurchaseOrderColumnHeader.PURCHASE_ORDER,
      column: (row: PurchaseOrder) => (
        <Link
          to={`${ROUTES_CONFIG.purchaseOrders.path}?id=${row.id}`}
          className="text-primary-900 hover:text-primary-700 hover:underline transition-colors"
        >
          {row.id}
        </Link>
      ),
      sticky: true
    },
    {
      header: PurchaseOrderColumnHeader.CONFIRMATION_STATUS,
      column: (row: PurchaseOrder) =>
        row.confirmationStatus ? (
          <span className="flex justify-center">
            <StatusPill
              status={row.confirmationStatus}
              linkPath={
                row.confirmationStatus === "TBC" || row.confirmationStatus === "Overdue"
                  ? `${ROUTES_CONFIG.updatePurchaseOrderConfirmation.path}?id=${row.id}`
                  : undefined
              }
            />
          </span>
        ) : (
          "N/A"
        ),
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.TRACKING_NUMBER,
      column: (row: PurchaseOrder) =>
        row.trackingNo ? (
          <div className="flex justify-center">
            <TrackingLink trackingNo={row.trackingNo} carrier={row.carrier} collectionOrder={row.collectionOrder} />
          </div>
        ) : (
          row.trackingNo
        ),
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.CARRIER_STATUS,
      column: (row: PurchaseOrder) => row.carrierStatus,
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.COMPLETION_STATUS,
      column: (row: PurchaseOrder) => (
        <span className="flex justify-center">
          <StatusPill status={row.completionStatus} />
        </span>
      ),
      styles: { textAlign: "center" }
    },
    /*
   TODO: temp re-add code later - once GXO fix erroneous check-ins
    {
      header: PurchaseOrderColumnHeader.CHECK_IN_STATUS,
      column: (row: PurchaseOrder) => row.checkInStatus && <StatusPill status={row.checkInStatus} />
    },
    */
    {
      header: PurchaseOrderColumnHeader.ESTIMATED_DELIVERY_DATE,
      column: (row: PurchaseOrder) =>
        row.carrierEstimatedDeliveryDate
          ? `${DD_MM_YYYY.formatDate(new Date(row.carrierEstimatedDeliveryDate))}\u00A0(Tracking)`
          : row.confirmationDetails?.estDeliveryDate
            ? `${DD_MM_YYYY.formatDate(new Date(row.confirmationDetails.estDeliveryDate))}\u00A0(Confirmed)`
            : undefined,
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.EST_COLLECTION_DATE,
      column: (row: PurchaseOrder) =>
        row.confirmationDetails?.estCollectionDate
          ? `${DD_MM_YYYY.formatDate(new Date(row.confirmationDetails.estCollectionDate))}\u00A0(Confirmed)`
          : undefined,
      styles: { textAlign: "center" }
    },
    // {
    //   header: PurchaseOrderColumnHeader.UPDATE_SHIPMENT_DETAILS,
    //   column: (row: PurchaseOrder) =>
    //     !PurchaseOrderService.isPOShipmentDeliveryDetailsLocked(row) && row.confirmationStatus === "Confirmed" ? (
    //       <EditPurchaseOrderBtn id={row.id} type={"UPDATE_SHIPMENT_DETAILS"} />
    //     ) : undefined
    // },
    {
      header: PurchaseOrderColumnHeader.ASN,
      column: (row: PurchaseOrder) => row.asn,
      styles: { whiteSpace: "nowrap", textAlign: "center" }
    },
    // {
    //   header: PurchaseOrderColumnHeader.PURCHASE_ORDER_PDF,
    //   column: (row: PurchaseOrder) =>
    //     row.confirmationStatus === "Confirmed" ? (
    //       <div style={{ position: "relative", display: "inline-block" }}>
    //         <FileTypeBadge fileType={row.fileType || VendorFileType.PDF} />
    //         <FileDownloadButton
    //           handleDownload={() => downloadFile(row.id, PurchaseOrderFileType.PDF)}
    //           isDisabled={isFileDownloading}
    //         />
    //       </div>
    //     ) : undefined
    // },
    // {
    //   header: PurchaseOrderColumnHeader.MORE_INFO,
    //   column: (row: PurchaseOrder) => <PageLinkButton path={`${ROUTES_CONFIG.purchaseOrders.path}?id=${row.id}`} />
    // },
    // {
    //   header: PurchaseOrderColumnHeader.INVOICE,
    //   column: (row: PurchaseOrder) =>
    //     row.hasInvoice ? (
    //       <FileDownloadButton
    //         handleDownload={() => downloadFile(row.id, PurchaseOrderFileType.INVOICES)}
    //         isDisabled={isFileDownloading}
    //       />
    //     ) : row.confirmationStatus === "Confirmed" ? (
    //       <EditPurchaseOrderBtn id={row.id} type={"UPDATE_INVOICE"} />
    //     ) : undefined
    // },
    {
      header: PurchaseOrderColumnHeader.LAST_UPDATE,
      column: (row: PurchaseOrder) => (row.updatedAt ? DD_MM_YYYY.formatDate(new Date(row.updatedAt)) : undefined),
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.CREATION_DATE,
      column: (row: PurchaseOrder) => (row.createdAt ? DD_MM_YYYY.formatDate(new Date(row.createdAt)) : undefined),
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.ACTIONS,
      column: (row: PurchaseOrder) => (
        <TooltipProvider>
          <div className="flex items-center justify-center gap-1">
            {/* Update Shipment Details */}
            {!PurchaseOrderService.isPOShipmentDeliveryDetailsLocked(row) && row.confirmationStatus === "Confirmed" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={`${ROUTES_CONFIG.updatePurchaseOrderShipmentDetails.path}?id=${row.id}`}
                    className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                  >
                    <Truck className="h-4 w-4 text-muted-600" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Update Shipment Details</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* PO File Download */}
            {row.confirmationStatus === "Confirmed" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 hover:bg-secondary-100 transition-colors"
                    onClick={() =>
                      downloadFile(
                        row.id,
                        row.fileType === VendorFileType.CSV ? PurchaseOrderFileType.CSV : PurchaseOrderFileType.PDF
                      )
                    }
                    disabled={isFileDownloading}
                  >
                    <FileText className="h-4 w-4 text-muted-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Download PO File ({row.fileType || VendorFileType.PDF})</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* More Info */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={`${ROUTES_CONFIG.purchaseOrders.path}?id=${row.id}`}
                  className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                >
                  <Info className="h-4 w-4 text-muted-600" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>More Info</p>
              </TooltipContent>
            </Tooltip>

            {/* Invoice */}
            {(row.hasInvoice || row.confirmationStatus === "Confirmed") && (
              <Tooltip>
                <TooltipTrigger asChild>
                  {row.hasInvoice ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-secondary-100 transition-colors"
                      onClick={() => downloadFile(row.id, PurchaseOrderFileType.INVOICES)}
                      disabled={row.hasInvoice && isFileDownloading}
                    >
                      <Download className="h-4 w-4 text-muted-600" />
                    </Button>
                  ) : (
                    <Link
                      to={`${ROUTES_CONFIG.updatePurchaseOrderInvoice.path}?id=${row.id}`}
                      className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                    >
                      <Receipt className="h-4 w-4 text-muted-600" />
                    </Link>
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  <p>{row.hasInvoice ? "Download Invoice" : "Upload Invoice"}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
      ),
      styles: { textAlign: "center" }
    }
  ];

  return (
    <Table<PurchaseOrderColumnHeader, PurchaseOrder>
      data={purchaseOrders}
      columns={purchaseOrdersTableColumns}
      columnsToHide={columnsToHide}
    />
  );
};

export { VendorsPurchaseOrderDashTable };
